import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { StatusBadge } from "@/components/StatusBadge";
import { Printer, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { APP_NAME, APP_FOOTER } from "@/lib/constants";

function IDR(n) { return new Intl.NumberFormat("id-ID", { style:"currency", currency:"IDR", maximumFractionDigits:0 }).format(n || 0); }

export default function LaporanRAKER() {
  const [decisions, setDecisions] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/decisions"),
      api.get("/programs"),
      api.get("/klasis-profile").catch(() => ({ data: null })),
    ]).then(([d, p, kp]) => {
      setDecisions(d.data.filter(x => x.source_meeting === "RAKER 22 Agustus 2026"));
      setPrograms(p.data);
      setProfile(kp.data);
      setLoading(false);
    });
  }, []);

  const selesai = decisions.filter(d => d.status === "SELESAI").length;
  const proses = decisions.filter(d => d.status === "PROSES").length;
  const belum = decisions.length - selesai - proses;
  const pct = decisions.length > 0 ? Math.round(selesai * 100 / decisions.length) : 0;

  const totalBudget = programs.reduce((s,p)=>s+(p.budget||0), 0);
  const totalRealisasi = programs.reduce((s,p)=>s+(p.realisasi||0), 0);
  const budgetPct = totalBudget > 0 ? Math.round(totalRealisasi*100/totalBudget) : 0;

  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Memuat laporan…</div>;

  return (
    <div className="min-h-screen bg-muted/30 py-8">
      <div className="max-w-4xl mx-auto px-4 mb-6 flex items-center justify-between print:hidden">
        <Link to="/dashboard" className="text-sm text-muted-foreground hover:text-primary flex items-center gap-1"><ArrowLeft className="h-4 w-4" /> Kembali</Link>
        <Button className="rounded-full" onClick={()=>window.print()} data-testid="btn-print-raker"><Printer className="h-4 w-4 mr-2" /> Cetak / Ekspor PDF</Button>
      </div>

      <div className="max-w-4xl mx-auto px-4" data-testid="laporan-raker">
        <div className="bg-white shadow-sm rounded-md">
          {/* Header */}
          <div className="bg-navy-gradient text-white p-8 rounded-t-md papua-motif relative">
            <div className="text-[0.65rem] tracking-[0.22em] uppercase text-accent font-bold">Laporan Progres • Internal</div>
            <h1 className="font-heading font-bold text-3xl mt-2">Progres Hasil RAKER 2026</h1>
            <div className="mt-2 text-sm opacity-90">BPK GPDP Klasis Mimika — Periode 2026–2031</div>
            <div className="mt-1 text-xs opacity-75">Rapat Kerja 22 Agustus 2026 • GPDP Jemaat El Olam Timika</div>
            <div className="mt-4 flex items-center gap-6 text-xs">
              <div><span className="opacity-75">Ketua Klasis:</span> <strong>Pdt. Silwanus Werimon</strong></div>
              <div><span className="opacity-75">Sekretaris:</span> <strong>Pdp. Algertho Reno Asmuruf, ST., MT</strong></div>
            </div>
          </div>

          <div className="p-8 space-y-8">
            {/* Executive summary */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="border border-border rounded-md p-3">
                <div className="text-[0.65rem] tracking-[0.18em] uppercase text-muted-foreground">Keputusan</div>
                <div className="font-heading font-bold text-xl text-primary">{decisions.length}</div>
              </div>
              <div className="border border-emerald-200 bg-emerald-50 rounded-md p-3">
                <div className="text-[0.65rem] tracking-[0.18em] uppercase text-emerald-700">Selesai</div>
                <div className="font-heading font-bold text-xl text-emerald-800">{selesai}</div>
              </div>
              <div className="border border-amber-200 bg-amber-50 rounded-md p-3">
                <div className="text-[0.65rem] tracking-[0.18em] uppercase text-amber-700">Proses</div>
                <div className="font-heading font-bold text-xl text-amber-900">{proses}</div>
              </div>
              <div className="border border-border rounded-md p-3">
                <div className="text-[0.65rem] tracking-[0.18em] uppercase text-muted-foreground">Belum</div>
                <div className="font-heading font-bold text-xl">{belum}</div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <div className="text-sm font-medium">Progres Tindak Lanjut Keputusan</div>
                <div className="text-sm font-mono font-semibold text-primary">{pct}%</div>
              </div>
              <Progress value={pct} className="h-2" />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <div className="text-sm font-medium">Realisasi Anggaran Program</div>
                <div className="text-sm font-mono font-semibold text-accent">{IDR(totalRealisasi)} / {IDR(totalBudget)} ({budgetPct}%)</div>
              </div>
              <Progress value={budgetPct} className="h-2" />
            </div>

            {/* Decisions detail */}
            <div>
              <h2 className="font-heading font-bold text-lg text-primary mb-3">17 Keputusan RAKER — Progres Tindak Lanjut</h2>
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="text-left p-2 border border-border w-8">#</th>
                    <th className="text-left p-2 border border-border">Keputusan</th>
                    <th className="text-left p-2 border border-border w-32">PIC</th>
                    <th className="text-left p-2 border border-border w-24">Jatuh Tempo</th>
                    <th className="text-left p-2 border border-border w-24">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {decisions.map((d, i) => (
                    <tr key={d.id} className="align-top">
                      <td className="p-2 border border-border font-mono">{i+1}</td>
                      <td className="p-2 border border-border">
                        <div className="font-semibold">{d.title.replace(/^\[RAKER\]\s*/, "")}</div>
                        <div className="text-muted-foreground text-[0.7rem] mt-0.5">{d.content}</div>
                      </td>
                      <td className="p-2 border border-border">{d.pic}</td>
                      <td className="p-2 border border-border">{d.due_date}</td>
                      <td className="p-2 border border-border"><StatusBadge status={d.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Programs */}
            <div>
              <h2 className="font-heading font-bold text-lg text-primary mb-3">Realisasi Program Kerja</h2>
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="text-left p-2 border border-border">Program</th>
                    <th className="text-left p-2 border border-border w-24">Bidang</th>
                    <th className="text-right p-2 border border-border w-32">Anggaran</th>
                    <th className="text-right p-2 border border-border w-32">Realisasi</th>
                    <th className="text-right p-2 border border-border w-16">%</th>
                  </tr>
                </thead>
                <tbody>
                  {programs.map((p) => {
                    const pctp = p.budget ? Math.min(100, Math.round((p.realisasi||0)*100/p.budget)) : 0;
                    return (
                      <tr key={p.id}>
                        <td className="p-2 border border-border">{p.name}</td>
                        <td className="p-2 border border-border text-[0.7rem]">{p.bidang}</td>
                        <td className="p-2 border border-border text-right font-mono">{p.budget ? IDR(p.budget) : "—"}</td>
                        <td className="p-2 border border-border text-right font-mono">{IDR(p.realisasi || 0)}</td>
                        <td className="p-2 border border-border text-right font-mono">{pctp}%</td>
                      </tr>
                    );
                  })}
                  <tr className="bg-primary/5 font-semibold">
                    <td colSpan={2} className="p-2 border border-border">TOTAL</td>
                    <td className="p-2 border border-border text-right font-mono">{IDR(totalBudget)}</td>
                    <td className="p-2 border border-border text-right font-mono">{IDR(totalRealisasi)}</td>
                    <td className="p-2 border border-border text-right font-mono">{budgetPct}%</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Signatures */}
            <div className="grid grid-cols-2 gap-6 pt-6 border-t border-border">
              <div>
                <div className="text-xs text-muted-foreground">Timika, {new Date().toLocaleDateString("id-ID", { day:"numeric", month:"long", year:"numeric" })}</div>
                <div className="text-sm font-semibold mt-1">Ketua Klasis</div>
                <div className="mt-12 border-b border-foreground/60 pb-1 font-semibold">Pdt. Silwanus Werimon</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Sekretariat</div>
                <div className="text-sm font-semibold mt-1">Sekretaris Klasis</div>
                <div className="mt-12 border-b border-foreground/60 pb-1 font-semibold">Pdp. Algertho Reno Asmuruf, ST., MT</div>
              </div>
            </div>
          </div>

          <div className="bg-muted/30 px-8 py-4 rounded-b-md text-[0.65rem] text-muted-foreground border-t border-border">
            {APP_NAME} • {APP_FOOTER} • {profile?.sk_bpk && <>SK: {profile.sk_bpk}</>}
          </div>
        </div>
      </div>
    </div>
  );
}
